import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { FiArrowLeft, FiChevronDown, FiMail } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
    createEmailManagementRequest,
    resetCreateEmailManagement,
} from '../../store/actions/emailManagementActions';
import { getAllPartners } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { FormError, FormLabel } from '../../components/Form';
import EmailManagementConfigFields from '../../components/Admin/EmailManagementConfigFields';
import EmailManagementTypeTabs from '../../components/Admin/EmailManagementTypeTabs';
import {
    EMAIL_MANAGEMENT_CONFIG_DEFAULTS,
    EMAIL_MANAGEMENT_TYPE,
    applyEmailManagementTypeChange,
    buildEmailManagementPayload,
    getEmailManagementConfigSchema,
} from '../../constants/emailManagementForm';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminEmailEdit.css';
import './AdminEmailCreate.css';

const createEmailManagementSchema = getEmailManagementConfigSchema().concat(
    yup.object({
        partner_id: yup.string().trim().required('Le partenaire est requis'),
    })
);

const getPartnerId = (partner) => partner.user_uuid || partner.partner_id || partner.id;

const getPartnerLabel = (partner) =>
    partner.company_name
    || `${partner.partner_first_name || partner.first_name || ''} ${partner.partner_last_name || partner.last_name || ''}`.trim()
    || partner.email
    || 'N/A';

const AdminEmailCreate = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { create: createState } = useSelector((state) => state.emailManagement);

    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [partnersError, setPartnersError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        watch,
        setValue,
    } = useForm({
        resolver: yupResolver(createEmailManagementSchema),
        defaultValues: {
            partner_id: '',
            ...EMAIL_MANAGEMENT_CONFIG_DEFAULTS,
        },
    });

    const emailList = watch('email_list') || [];
    const emailManagementType = watch('email_management_type') || EMAIL_MANAGEMENT_TYPE.STANDARD;
    const isSaving = Boolean(createState?.loading || isSubmitting);
    const isStandard = emailManagementType === EMAIL_MANAGEMENT_TYPE.STANDARD;

    useEffect(() => {
        dispatch(resetCreateEmailManagement());
        return () => dispatch(resetCreateEmailManagement());
    }, [dispatch]);

    useEffect(() => {
        const fetchPartners = async () => {
            try {
                setPartnersLoading(true);
                setPartnersError(null);
                const response = await getAllPartners();
                let partnersList = [];
                if (response?.users && Array.isArray(response.users)) {
                    partnersList = response.users;
                } else if (Array.isArray(response)) {
                    partnersList = response;
                } else if (response?.partners && Array.isArray(response.partners)) {
                    partnersList = response.partners;
                } else if (response?.data && Array.isArray(response.data)) {
                    partnersList = response.data;
                }
                setPartners(partnersList);
            } catch (error) {
                setPartners([]);
                setPartnersError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
            } finally {
                setPartnersLoading(false);
            }
        };
        fetchPartners();
    }, []);

    useEffect(() => {
        if (createState.success) {
            setSuccessMessage('Email management créé avec succès !');
            const timer = setTimeout(() => navigate('/admin/email'), 1500);
            return () => clearTimeout(timer);
        }
        if (createState.error) {
            setSuccessMessage(null);
        }
    }, [createState.success, createState.error, navigate]);

    const onSubmit = (data) => {
        dispatch(createEmailManagementRequest(
            data.partner_id,
            buildEmailManagementPayload(data)
        ));
    };

    return (
        <div className="dashboard-page admin-email-create-page">
            <div className="aee-page">
                <div className="aee-page__header">
                    <h1 className="aee-page__title">
                        <FiMail aria-hidden="true" />
                        Créer un Email Management
                    </h1>
                    <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                        <FiArrowLeft className="me-2" /> Retour
                    </Button>
                </div>

                <div className="aee-type-tabs-wrap">
                    <EmailManagementTypeTabs
                        value={emailManagementType}
                        disabled={isSaving || partnersLoading}
                        onChange={(value) => applyEmailManagementTypeChange(setValue, value)}
                    />
                </div>

                {createState?.error && (
                    <Alert variant="danger" className="mb-0">
                        {createState.error}
                    </Alert>
                )}
                {successMessage && (
                    <Alert variant="success" className="mb-0">
                        {successMessage}
                    </Alert>
                )}
                {partnersError && (
                    <Alert variant="danger" className="mb-0">
                        {partnersError}
                    </Alert>
                )}

                {partnersLoading ? (
                    <div className="aee-loading">
                        <Spinner animation="border" variant="primary" />
                        <p>Chargement des partenaires…</p>
                    </div>
                ) : (
                    <form className="aee-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                        <input type="hidden" {...register('email_management_type')} />
                        <section className="aee-section" aria-labelledby="aee-create-section-title">
                            <header className="aee-section__header">
                                <h2 id="aee-create-section-title" className="aee-section__title">
                                    {isStandard ? 'Configuration Standard' : 'Configuration Spécifique'}
                                </h2>
                                <p className="aee-section__desc">
                                    {isStandard
                                        ? 'Sélectionnez le partenaire et les e-mails autorisés.'
                                        : 'Sélectionnez le partenaire et le processus email.'}
                                </p>
                            </header>

                            <div className="aee-section__body">
                                <EmailManagementConfigFields
                                    register={register}
                                    errors={errors}
                                    setValue={setValue}
                                    emailList={emailList}
                                    isSaving={isSaving}
                                    emailManagementType={emailManagementType}
                                    partnerField={(
                                        <div className="ow-form-field">
                                            <FormLabel text="Partenaire" htmlFor="partner_id" required />
                                            <div className="ow-form-select-wrap">
                                                <select
                                                    id="partner_id"
                                                    className={[
                                                        'ow-form-select',
                                                        errors.partner_id && 'ow-form-select--error',
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' ')}
                                                    {...register('partner_id')}
                                                    disabled={isSaving}
                                                    aria-required="true"
                                                    aria-invalid={Boolean(errors.partner_id)}
                                                >
                                                    <option value="">Sélectionner un partenaire...</option>
                                                    {partners.map((partner) => {
                                                        const partnerId = getPartnerId(partner);
                                                        return (
                                                            <option key={partnerId} value={partnerId}>
                                                                {getPartnerLabel(partner)}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                                <FiChevronDown className="ow-form-select-chevron" aria-hidden="true" />
                                            </div>
                                            <FormError message={errors.partner_id?.message} />
                                        </div>
                                    )}
                                />
                            </div>
                        </section>

                        <div className="aee-footer" role="group" aria-label="Actions du formulaire">
                            <div className="aee-footer__actions">
                                <Button
                                    variant="outline-secondary"
                                    type="button"
                                    onClick={() => navigate(-1)}
                                    disabled={isSaving}
                                >
                                    Annuler
                                </Button>
                                <Button variant="primary" type="submit" disabled={isSaving}>
                                    {isSaving ? (
                                        <>
                                            <Spinner size="sm" className="me-2" animation="border" />
                                            Création...
                                        </>
                                    ) : (
                                        'Créer'
                                    )}
                                </Button>
                            </div>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AdminEmailCreate;
